import { TFunction } from 'i18next';
import { z } from 'zod';
import { TextInput, useToasts } from '@applyboard/crystal-ui';
import { useTranslation } from '@applyboard/ui-utils';
import { zodValidateField } from '../schema/forms';
import { htmlDecode } from '../utils/decodeHtml';

export const DEFAULT_STRING_FIELD_REGEX = /^$|^[^&<>@:~#%^*$\\{}|=]+$/g;

export function getForbiddenCharsInString(regex: RegExp, input: string): string[] {
  return [
    ...new Set(
      Array.from(input).filter(char => {
        regex.lastIndex = 0;
        return !regex.test(char);
      }),
    ),
  ];
}

interface InputWithCharacterFilterProps {
  Field: React.ElementType;
  fieldName: string;
  fieldLabel: string;
  zodSchema?: (t: TFunction) => z.ZodString | z.ZodOptional<z.ZodString>;
  required?: string | boolean;
  defaultRegexOverride?: RegExp;
  skipValidationIfEmptyValue?: boolean;
  disabled?: boolean;
  validate?: (value: string, options: { values: Record<string, unknown> }) => string | boolean;
}

const TextInputWithFilter = ({
  Field,
  fieldName,
  fieldLabel,
  zodSchema,
  required,
  defaultRegexOverride,
  skipValidationIfEmptyValue,
  disabled,
  validate,
}: InputWithCharacterFilterProps) => {
  const { t } = useTranslation();
  const toasts = useToasts();

  const specialCharactersSchema = (): z.ZodString => {
    let schema = z
      .string()
      .trim()
      .regex(
        defaultRegexOverride ?? DEFAULT_STRING_FIELD_REGEX,
        t('common.errors.specialCharactersNotAllowed'),
      );

    if (required === true) {
      schema = schema.min(
        1,
        t('common.errors.required', {
          fieldName: fieldLabel,
        }),
      );
    } else if (required) {
      schema = schema.min(1, required);
    }

    return schema;
  };

  const finalSchema = required ? specialCharactersSchema() : specialCharactersSchema().optional();

  const requiredProps =
    required === true
      ? {
          required: t('common.errors.required', {
            fieldName: fieldLabel,
          }),
        }
      : typeof required === 'string' && required
        ? { required }
        : {};

  return (
    <Field
      as={TextInput}
      name={fieldName}
      label={fieldLabel}
      {...requiredProps}
      disabled={disabled}
      validate={(value: string, options: { values: Record<string, unknown> }) => {
        const regexZodValidateResult = zodValidateField(finalSchema, fieldName, value, toasts);

        if (skipValidationIfEmptyValue && !value) {
          return true;
        }

        if (regexZodValidateResult !== true) {
          // Check if the error message is due to special characters
          if (regexZodValidateResult === t('common.errors.specialCharactersNotAllowed')) {
            // Extract forbidden characters from the value
            const forbiddenInValue = getForbiddenCharsInString(
              defaultRegexOverride ?? DEFAULT_STRING_FIELD_REGEX,
              value,
            );
            const errorMessage = t('common.errors.specialCharactersNotAllowed', {
              fieldLabel: fieldLabel,
              characters: forbiddenInValue.join(' '),
            });
            return htmlDecode(errorMessage);
          }

          return regexZodValidateResult;
        }

        if (zodSchema) {
          const customZodValidateResult = zodValidateField(zodSchema(t), fieldName, value, toasts);
          if (customZodValidateResult !== true) {
            return customZodValidateResult;
          }
        }

        validate?.(value, options);
        return true;
      }}
    />
  );
};

export default TextInputWithFilter;
